import { useCallback } from "react";
import {
  useVerifyTransactionRiskMutation,
  useLoginRecordMutation,
  useRiskRecordMutation,
} from "src/store/features/api/generated";
import { useTSAccountProtection } from "@transmitsecurity/riskid-reactjs-ts";
import { getCorrelationID, getIdToken } from "../SessionManagement";
import { clearTransmitInitError, getTransmitInitError } from "../transmitInitError";

interface TransactionData {
  amount?: number;
  currency?: string;
  reason?: string;
  transactionId?: string;
}

const hashEmail = async (email: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

const toErrorStack = (stack: string | undefined): object | undefined =>
  stack ? { stack } : undefined;

const useTransmitRisk = () => {
  const { triggerActionEvent } = useTSAccountProtection();
  const [verifyTransactionRisk] = useVerifyTransactionRiskMutation();
  const [loginRecord] = useLoginRecordMutation();
  const [riskRecord] = useRiskRecordMutation();

  const executeTransmitRisk = useCallback(
    async (event: string, email?: string, transactionData?: TransactionData) => {
      if (typeof triggerActionEvent !== "function") {
        const initError = getTransmitInitError();
        const errorReason = initError?.reason ?? "TSAccountProtection failed to initialise — triggerActionEvent is unavailable";
        console.error(`[TransmitRisk] SDK init failure surfaced at action "${event}":`, errorReason);
        riskRecord({
          riskFailureRecordDto: {
            actionType: event,
            errorReason,
            errorStack: toErrorStack(initError?.stack),
            ...(email && { email }),
          },
        });
        clearTransmitInitError();
        return null;
      }

      const token = getIdToken();
      if (!token) {
        console.error(`[TransmitRisk] ID token not found (${event}), user is not authenticated`);
        return null;
      }

      try {
        const claimedUserId = email ? await hashEmail(email) : undefined;
        const actionResponse = await triggerActionEvent(event, {
          ...(claimedUserId && { claimedUserId, claimedUserIdType: "email" }),
          correlationId: getCorrelationID(),
        });

        const resolvedActionToken = actionResponse?.actionToken;
        if (!resolvedActionToken) {
          throw new Error("Action token not received");
        }

        const data = await verifyTransactionRisk({
          transactionRiskReqDto: {
            actionToken: resolvedActionToken,
            idToken: token,
            actionType: event,
            transactionData,
          },
        }).unwrap();

        return data?.riskScore ?? null;
      } catch (error: any) {
        const backendMessage =
          typeof error?.data?.message === "string" ? error.data.message : undefined;
        const message =
          backendMessage || error?.message || "Verification temporarily unavailable";

        console.error(`Risk action error (${event}): ${message}`, error);

        // Fire-and-forget error records — not awaited intentionally.
        if (event === "login") {
          loginRecord({
            loginRecordDto: {
              errorReason: message,
              errorStack: toErrorStack(error?.stack || JSON.stringify(error) || "Not available"),
              timestamp: Date.now(),
            },
          });
        }

        riskRecord({
          riskFailureRecordDto: {
            actionType: event,
            errorReason: message,
            errorStack: toErrorStack(error?.stack || JSON.stringify(error) || "Not available"),
          },
        });

        return null;
      }
    },
    [triggerActionEvent, verifyTransactionRisk, loginRecord, riskRecord],
  );

  return { executeTransmitRisk };
};

export default useTransmitRisk;
